import React, {createContext, ReactNode, useCallback, useContext, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {AuthState, LoginResponse, RefreshResponse, User, userClaims} from '../types/auth';
import api from "../services/api";
import {refreshTokens, removeTokens, setAuthContextUpdater, updateTokens} from "../services/authService";
import {jwtDecode} from "jwt-decode";

interface AuthContextType {
    user: User | null;
    auth: AuthState;
    login: (username: string, password: string) => Promise<void>;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({children}: { children: ReactNode }) => {
    const [user, setUser] = useState<User | null>(null);
    const [auth, setAuth] = useState<AuthState>(() => ({
        accessToken: localStorage.getItem('accessToken'),
        refreshToken: localStorage.getItem('refreshToken'),
        isAuthenticated: !!localStorage.getItem('accessToken'),
    }));

    const navigate = useNavigate();

    const login = async (username: string, password: string) => {

        const response = await api.post<LoginResponse>('/api/auth/login', {username, password});

        const refreshResponse = {
            accessToken: response.data.accessToken,
            refreshToken: response.data.refreshToken,
            idToken: response.data.idToken,
        } as RefreshResponse;

        updateTokens(refreshResponse);

        const accessToken = refreshResponse.accessToken;
        const refreshToken = refreshResponse.refreshToken;

        setAuth({accessToken, refreshToken, isAuthenticated: true});
        navigate('/dashboard');
    };

    const logout = useCallback(() => {
        removeTokens();
        setAuth({accessToken: null, refreshToken: null, isAuthenticated: false});
        setUser(null);
        navigate('/login');
    }, [navigate]);

    // auto-refresh tokens
    useEffect(() => {
        if (!auth.isAuthenticated || !auth.refreshToken) return;
        const interval = setInterval(async () => {
            try {
                const response = await refreshTokens()
                if (response === false) {
                    console.error('Refresh failed');
                    logout();
                } else {
                    setAuth((prev) => ({
                        ...prev,
                        accessToken: response.accessToken,
                        refreshToken: response!.refreshToken,
                    }));
                }

            } catch (error) {
                console.error('Refresh failed', error);
                logout();
            }
        }, 10 * 60 * 1000); // refresh every 10 minutes

        return () => clearInterval(interval);
    }, [auth.isAuthenticated, auth.refreshToken, logout]);

    // auto-update user, when idToken is refreshed
    useEffect(() => {
        setAuthContextUpdater((newToken) => {
            if (newToken) {
                try {
                    const decoded = jwtDecode(newToken) as any;
                    setUser({
                        id: decoded[userClaims.id],
                        name: decoded[userClaims.name],
                    });
                } catch (e) {
                    console.error('Invalid token', e);
                    logout();
                }
            }
        });
    }, [logout]);

    // auto-set user on loading page
    useEffect(() => {
        if (auth.accessToken) {
            try {
                const decoded = jwtDecode(auth.accessToken) as any;
                setUser({
                    id: decoded[userClaims.id],
                    name: decoded[userClaims.name],
                });
            } catch (e) {
                console.error('Invalid token', e);
                logout();
            }
        }
    }, [auth.accessToken, logout]);

    return (
        <AuthContext.Provider value={{user, auth, login, logout}}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
